import React, { useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import type { PropertyTerms } from 'api/types';
import type { SelectOption } from 'components/ui/SyutSelect';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface PropertyTermsViewModalProps {
  isOpen: boolean;
  record: PropertyTerms | null;
  onClose: () => void;
  onEdit?: (record: PropertyTerms) => void;
  termTypeOptions?: SelectOption[];
  // Minimize-to-bar support
  tabId?: string;
  moduleName?: string;
  onRestoreView?: (recordId: string) => void;
}

// ============================================================================
// READ-ONLY FIELD
// ============================================================================

const ReadOnlyField: React.FC<{ label: string; value?: string | number | boolean | null; span?: boolean }> = ({ label, value, span }) => (
  <div style={span ? { gridColumn: '1 / -1' } : undefined}>
    <label className="form-label">{label}</label>
    <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--form-border-color)' }}>
      {value != null && value !== '' ? (typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)) : '-'}
    </div>
  </div>
);

const languageLabelMap: Record<string, string> = {
  en: 'English', hi: 'Hindi', ar: 'Arabic', fr: 'French', de: 'German', es: 'Spanish',
};

// ============================================================================
// COMPONENT
// ============================================================================

const PropertyTermsViewModal: React.FC<PropertyTermsViewModalProps> = ({
  isOpen,
  record,
  onClose,
  onEdit,
  termTypeOptions = [],
  tabId,
  moduleName,
  onRestoreView,
}) => {
  const { minimize } = useModalMinimize();

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName || !record) return;
    const termTypeLabel = termTypeOptions.find(o => o.value === record.termType)?.label || record.termType;
    minimize({
      id: `${tabId}-view-modal-${record.id}`,
      title: `View - ${termTypeLabel}`,
      moduleName,
      tabId,
      onRestore: () => onRestoreView?.(record.id),
    });
    onClose();
  }, [tabId, moduleName, record, termTypeOptions, minimize, onClose, onRestoreView]);

  if (!isOpen || !record) return null;

  const termTypeLabel = termTypeOptions.find(o => o.value === record.termType)?.label || record.termType;
  const languageLabel = languageLabelMap[record.languageCode] || record.languageCode?.toUpperCase() || '-';
  const propertyDisplay = record.propertyCode && record.propertyName
    ? `${record.propertyCode} - ${record.propertyName}`
    : record.propertyId;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ minHeight: 'auto' }}>
        <div className="modal-header">
          <h2 className="modal-title">Property Terms Details</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar">
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={onClose}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {/* --- Property, Term Type, Language --- */}
          <div className="form-grid-3">
            <ReadOnlyField label="Property" value={propertyDisplay} />
            <ReadOnlyField label="Term Type" value={termTypeLabel} />
            <ReadOnlyField label="Language" value={languageLabel} />
          </div>

          {/* --- Terms Text (formatted with line breaks) --- */}
          <div style={{ marginTop: '1rem' }}>
            <label className="form-label">Terms Text</label>
            <div style={{
              padding: '0.75rem',
              background: 'var(--bg-secondary)',
              borderRadius: '0.375rem',
              border: '1px solid var(--form-border-color)',
              whiteSpace: 'pre-line',
              lineHeight: '1.6',
              fontSize: '0.85rem',
              maxHeight: '300px',
              overflowY: 'auto',
            }}>
              {record.termsText || '-'}
            </div>
          </div>

          {/* --- Valid From / Valid To / Status --- */}
          <div className="form-grid-3" style={{ marginTop: '1rem' }}>
            <ReadOnlyField
              label="Valid From"
              value={record.validFrom ? new Date(record.validFrom).toLocaleDateString() : undefined}
            />
            <ReadOnlyField
              label="Valid To"
              value={record.validTo ? new Date(record.validTo).toLocaleDateString() : undefined}
            />
            <div>
              <label className="form-label">Status</label>
              <div>
                <span className={`badge badge-${record.isActive ? 'active' : 'inactive'}`}>
                  {record.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>

          {/* --- Audit Info --- */}
          <div style={{ marginTop: '1.25rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
            <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.75rem' }}>
              Audit Information
            </h3>
            <div className="form-grid-2">
              <ReadOnlyField label="Created At" value={record.createdAt ? new Date(record.createdAt).toLocaleString() : undefined} />
              <ReadOnlyField label="Created By" value={record.createdBy} />
              <ReadOnlyField label="Updated At" value={record.updatedAt ? new Date(record.updatedAt).toLocaleString() : undefined} />
              <ReadOnlyField label="Updated By" value={record.updatedByName || record.updatedBy} />
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          {onEdit && (
            <button className="btn btn-primary" onClick={() => {
              onClose();
              onEdit(record);
            }}>
              <IconSystem.edit size={16} />
              Edit
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default PropertyTermsViewModal;
